import { useStoreState, useStoreActions } from 'easy-peasy';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { withTheme } from 'styled-components';

import { Button } from '@ge/components/button';
import { Checkbox, CheckedState } from '@ge/components/checkbox';
import { Icon, Icons } from '@ge/components/icon';
import { Loader } from '@ge/components/loader';
import { EntityType, TabFlowModelVariables, EntityAll } from '@ge/models/constants';
import { globalColors, StatusColor } from '@ge/tokens/colors';

import { FormError } from '../../../../entity-create/entity-create-shared';
import { useEntitiesFiltered } from '../../../../entity-create/hooks/use-entities-filtered';
import { usePatchPerson } from '../../hooks/use-patch-person';

import AllUserRolesComponent from './all-users-roles/all-user-roles-component';
import ViewModeDropdownComponent from './all-users-roles/view-mode-dropdown-component';
import { PersonAssetsGroupServiceGroup } from './person-assets-service-group';
import { PersonAssetsSites } from './person-assets-sites';
import WorkerAssetsComponent from './worker-assets/worker-assets-component';

const LoadingWrapper = styled.div`
  position: relative;
  margin: 0;
  width: 100%;
  height: 50vh;
`;
const DynamicAll = styled.div`
  position: relative;
  margin: 0;
  width: 100%;
  height: auto;
  &.hidden {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }
`;
const StyledCheckbox = styled(Checkbox)`
  align-items: center;
  &.read-only {
    > div {
      opacity: 0.6;
      visibility: hidden;
    }
    > span {
      opacity: 0.6;
      visibility: hidden;
    }
  }
`;

const StyledErrorIcon = styled(Icon).attrs(() => ({
  size: 24,
  color: `${StatusColor.DANGER}`,
}))``;

const PersonAssetsComponent = withTheme(({ theme }) => {
  const { iconColor } = theme.themeSelector;
  const {
    role,
    activeTab,
    editStatus,
    viewByServiceGroupsOrSites,
    person,
    filterByServiceGroup,
    filterBySite,
    tabIterator,
    selectedSiteIds,
    selectedServiceGroupIds,
    isApplyAllSites,
    personAssets,
    existingSiteIds,
    existingServiceGroupIds,
    ids,
    ...rest
  } = useStoreState((state) => state.tabflow.personAssets);
  const isCheckboxApplied =
    (tabIterator[activeTab] && isApplyAllSites && isApplyAllSites[tabIterator[activeTab]]) || false;
  const { control } = useFormContext();
  let { entitiesFiltered: sites } = useEntitiesFiltered(
    EntityType.SITE,
    filterBySite,
    tabIterator[activeTab],
  );
  let { entitiesFiltered: newServiceGroups } = useEntitiesFiltered(
    EntityType.SERVICE_GROUP,
    filterByServiceGroup,
    tabIterator[activeTab],
  );
  let isServiceGroupAndSitesLoading = useStoreState(
    (state) => state.sites.isServiceGroupAndSitesLoading,
  );
  const { changeState, addPersonData, refreshIds, cancelActions } = useStoreActions(
    (actions) => actions.tabflow,
  );
  const { t } = useTranslation(['admin.people']);
  const history = useHistory();
  const location = useLocation();
  const [isError, setError] = useState(false)
  const changeEditStatus = (editStatus) => {
    if (editStatus) {
      changeState({ name: TabFlowModelVariables.EDITSTATUS, value: false });
    } else {
      changeState({ name: TabFlowModelVariables.EDITSTATUS, value: true });
    }
  };
  const finishEditMode = (status, isEditError) => {
    if (status) {
      if (isEditError) {
        setError(true)
        return false
      }
      setError(false)
      changeState({ name: TabFlowModelVariables.EDITSTATUS, value: false });
      const returnSyncData = addPersonData('save');
      if (returnSyncData !== false) {
        refreshDataAndClose(returnSyncData);
      }
    } else {
      setError(false)
      cancelActions('cancel');
      changeState({ name: TabFlowModelVariables.EDITSTATUS, value: false });
    }
  };
  const lastActions = useCallback(() => {
    history.push(location.pathname);
  }, [history, location.pathname]);
  const successPatch = () => {
    refreshIds();
  };
  const { patch } = usePatchPerson({
    onSuccess: successPatch,
  });
  const refreshDataAndClose = useCallback(
    (returnSyncData) => {
      const { username } = person;
      patch({ username, body: returnSyncData });
      lastActions();
    },
    [lastActions, patch, person],
  );
  let viewByItems = [
    {
      value: TabFlowModelVariables.VIEWBY.GROUPS,
      label: t('create.service_groups', 'Service Groups'),
    },
    {
      value: TabFlowModelVariables.VIEWBY.SITES,
      label: t('create.sites', 'Sites'),
    },
  ];

  const handleApplyAllChange = (e) => {
    let value = { ...isApplyAllSites };
    value[tabIterator[activeTab]] = e;
    changeState({
      name: TabFlowModelVariables.ISAPPLYALLSITES,
      value,
    });
  };

  useEffect(() => {
    if (editStatus) {
      let isAllSelected = false;
      if (
        existingSiteIds.includes(EntityAll.ALL) ||
        existingServiceGroupIds.includes(EntityAll.ALL)
      )
        isAllSelected = true;
      let value = { ...isApplyAllSites };
      value[tabIterator[activeTab]] = isAllSelected;
      changeState({
        name: TabFlowModelVariables.ISAPPLYALLSITES,
        value,
      });
    }
  }, [editStatus, existingSiteIds, existingServiceGroupIds]);

  const checkIfNewRoleIsAdded = () => {
    let state = false
    if (editStatus) {
      ids.forEach((eachRole) => {
        if (eachRole.roleNameStatus === 0) {
          if (eachRole.role.groupCombination?.length &&
            !eachRole.role.groupCombination[0]?.ids?.length && !eachRole.role.groupCombination[0]?.add?.length &&
            !eachRole.role.groupCombination[1]?.ids?.length && !eachRole.role.groupCombination[1]?.add?.length &&
            !isApplyAllSites[eachRole.role.roleName])
            state = true
        }
      })
    }
    return state
  }

  return (
    <div className="person-assets-container">
      {editStatus &&
        !(
          tabIterator[activeTab] === TabFlowModelVariables.ROLESTYPE.WORKER &&
          (role === TabFlowModelVariables.ROLESTYPE.WORKER ||
            role === TabFlowModelVariables.ROLESTYPE.USER_AND_WORKER)
        ) && (
          <DynamicAll>
            <Controller
              control={control}
              defaultValue={isCheckboxApplied}
              name={`userDynamicAll-${activeTab}`}
              render={() => {
                return (
                  <StyledCheckbox
                    label={t('apply_assets.dynamic_all', 'Apply ALL SITES in the tenant')}
                    checkState={isCheckboxApplied ? CheckedState.CHECKED : CheckedState.UNCHECKED}
                    onChange={(e) => handleApplyAllChange(e)}
                    value={isCheckboxApplied ? CheckedState.CHECKED : CheckedState.UNCHECKED}
                  />
                );
              }}
            />
          </DynamicAll>
        )}
      {editStatus === true && role === TabFlowModelVariables.ROLESTYPE.USER && (
        <AllUserRolesComponent dropdownItems={viewByItems} />
      )}
      {editStatus === true &&
        role === TabFlowModelVariables.ROLESTYPE.USER_AND_WORKER &&
        activeTab === 0 && <WorkerAssetsComponent />}
      {editStatus === true &&
        role === TabFlowModelVariables.ROLESTYPE.USER_AND_WORKER &&
        activeTab > 0 && <AllUserRolesComponent dropdownItems={viewByItems} />}
      {editStatus === true &&
        role === TabFlowModelVariables.ROLESTYPE.WORKER &&
        activeTab === 0 && <WorkerAssetsComponent />}
      {editStatus === true && role === TabFlowModelVariables.ROLESTYPE.WORKER && activeTab > 0 && (
        <AllUserRolesComponent dropdownItems={viewByItems} />
      )}
      <div className="person-assets-frame">
        {editStatus === false && (
          <>
            <div className="person-assets-header-container">
              <div className="person-assets-one">
                <div>{t('assets.ASSETS', 'ASSETS')}</div>
                <div className="person-assets-text-m">
                  {t('assets.assets_applied', 'Assets applied to')} {person.firstName} {role}{' '}
                  {t('assets.assets_role', 'Role(s)')}
                </div>
              </div>
              <div onClick={() => changeEditStatus(editStatus)} className="person-assets-two">
                <Icon icon={Icons.PENCIL} size={11} color={iconColor} />
              </div>
            </div>
            <ViewModeDropdownComponent dropdownItems={viewByItems} />
          </>
        )}
        {viewByServiceGroupsOrSites === TabFlowModelVariables.VIEWBY.GROUPS && (
          <div className="person-assets-assets" style={{ color: globalColors.grey4 }}>
            {t('assets.number_of_assets_edit_flow', '# of Sites')}
          </div>
        )}
        {viewByServiceGroupsOrSites === TabFlowModelVariables.VIEWBY.SITES && (
          <div className="person-assets-assets" style={{ color: globalColors.grey4 }}></div>
        )}
        {isServiceGroupAndSitesLoading ? (
          <LoadingWrapper>
            <Loader />
          </LoadingWrapper>
        ) : viewByServiceGroupsOrSites === TabFlowModelVariables.VIEWBY.SITES ? (
          <PersonAssetsSites sites={sites} isDisableChildren={isCheckboxApplied} />
        ) : viewByServiceGroupsOrSites === TabFlowModelVariables.VIEWBY.GROUPS ? (
          <PersonAssetsGroupServiceGroup
            serviceGroups={newServiceGroups}
            isDisableChildren={isCheckboxApplied}
          />
        ) : null}
        {editStatus && (newServiceGroups.length || sites.length) && (
          <div className="person-assets-group-buttons">
            <Button
              onClick={() => finishEditMode(false)}
              type="button"
              className="person-assets-group-buttons-left-items"
            >
              {t('assets.Cancel', 'Cancel')}
            </Button>
            <Button
              primary
              onClick={() => finishEditMode(true, checkIfNewRoleIsAdded())}
              type="button"
              className="person-assets-group-button-right-items"
            >
              {t('assets.Save', 'Save')} {role}
            </Button>
          </div>
        )}
        {isError && (
          <FormError>
            <StyledErrorIcon icon={Icons.ALERT_NEW} />{' '}
            Role(s) cannot be added without Asset(s)
          </FormError>
        )}
      </div>
    </div>
  );
});
export default PersonAssetsComponent;
